/**
 * In-memory comment screen for screens and tests (VITE_USE_MOCK). Data follows the approved design (Comments5):
 * three posts/ads, comments waiting, answered and hidden, with the AI's reading and suggested replies.
 * Follows the server's rules: one private reply per comment within 7 days, a reply goes under the comment,
 * hide/skip move the comment between tabs. Names are made up.
 */
import { ApiError } from '../client'
import type {
  CannedLine,
  CommentCountsResponse,
  CommentDetail,
  CommentIntent,
  CommentItem,
  CommentListQuery,
  CommentPage,
  CommentReplyBody,
  CommentsApi,
  CommentSettings,
  CommentState,
  CommentThread,
  IntentKey,
  StaffRef,
  ThreadNode,
} from './types'

const MIN = 60_000
const DAY = 86_400_000
const clone = <T>(v: T): T => (v === null || v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T))
const iso = (ms: number): string => new Date(ms).toISOString()

const STAFF: Record<string, StaffRef> = {
  ploy: { id: 'ploy', name: 'พลอย' },
  jit: { id: 'jit', name: 'จิต' },
  siri: { id: 'siri', name: 'ศิริ' },
}

const INTENTS: Record<IntentKey, CommentIntent> = {
  buy: { key: 'buy', label: 'สนใจซื้อ', tone: 'ok' },
  question: { key: 'question', label: 'คำถามทั่วไป', tone: 'q' },
  complaint: { key: 'complaint', label: 'ร้องเรียน', tone: 'crit' },
  spam: { key: 'spam', label: 'สแปม', tone: 'mute' },
  social: { key: 'social', label: 'ทักทาย/ชม', tone: 'mute' },
}

const ORDER_LINK: CannedLine = {
  id: 'order_link',
  label: 'ส่งลิงก์สั่งซื้อ',
  text: 'สั่งได้ที่ลิงก์นี้เลยค่ะ cleanfit.cloud/order (สั่งทางเว็บได้ถึง 21.30 น. สำหรับรอบส่งวันถัดไปค่ะ)',
}
const ASK_ZIP: CannedLine = { id: 'ask_zip', label: 'ขอรหัสไปรษณีย์', text: 'รบกวนขอรหัสไปรษณีย์ด้วยนะคะ จะได้เช็คค่าส่งและรอบส่งให้ค่ะ' }
const WAIT_CHECK: CannedLine = { id: 'wait_check', label: 'ขอเวลาเช็ค', text: 'รอสักครู่นะคะ แอดมินขอเช็คข้อมูลให้ก่อนค่ะ' }
const ASK_ORDER: CannedLine = {
  id: 'ask_order',
  label: 'ขอเบอร์ที่ใช้สั่ง',
  text: 'รบกวนขอเบอร์โทรที่ใช้สั่ง หรือเลขออเดอร์ด้วยนะคะ แอดมินจะเช็คให้ค่ะ',
}
const THANKS: CannedLine = { id: 'thanks', label: 'ขอบคุณ', text: 'ขอบคุณมากค่ะ มีอะไรสอบถามเพิ่มเติมทักมาได้เลยนะคะ' }
const PUB_CHAT: CannedLine = { id: 'pub_chat', label: 'ชวนทักแชท', text: 'สนใจสอบถามรายละเอียดทางแชทของเพจได้เลยนะคะ แอดมินยินดีให้ข้อมูลค่ะ' }

interface MPost {
  id: string
  channel: 'fb' | 'ig'
  caption: string
  title: string
  is_ad: boolean
  ad_title: string | null
  posted: number
  permalink: string
}

interface MComment {
  id: string
  post: string
  name: string
  initial: string
  text: string
  at: number
  intent: IntentKey
  summary: string
  note?: string
  draftPub?: string
  draftDm?: string
  pub?: { text: string; at: number; by: string | null }
  dm?: { text: string; at: number; by: string }
  skip?: { at: number; by: string }
  hidden?: { by: string | null }
  unread: boolean
  pos?: boolean
  conv: string
  dmConv?: string
}

function seed(now: number): { posts: MPost[]; comments: MComment[] } {
  const t = (minAgo: number) => now - minAgo * MIN
  const posts: MPost[] = [
    {
      id: 'p1', channel: 'fb', title: 'แอด แพ็ก 7 วัน ก.ย.', is_ad: true, ad_title: 'แอด แพ็ก 7 วัน ก.ย.', posted: now - 6 * DAY,
      caption: 'อาหารคลีนส่งถึงบ้าน แพ็ก 7 วัน เลือกเมนูเองได้ ปรับเมนูให้คนแพ้อาหารได้ สั่งก่อน 21:30 ได้ของพรุ่งนี้',
      permalink: 'https://www.facebook.com/',
    },
    {
      id: 'p2', channel: 'ig', title: 'แอด เมนูเจ 2569', is_ad: true, ad_title: 'แอด เมนูเจ 2569', posted: now - 4 * DAY,
      caption: 'เทศกาลกินเจปีนี้ Cleanfit มีเมนูเจให้เลือกทั้งแพ็ก อิ่มสบายท้อง ไม่มีเนื้อสัตว์ ส่งถึงบ้านทุกวัน',
      permalink: 'https://www.instagram.com/',
    },
    {
      id: 'p3', channel: 'fb', title: 'เมนูใหม่สัปดาห์นี้', is_ad: false, ad_title: null, posted: now - 3 * DAY,
      caption: 'เมนูใหม่สัปดาห์นี้ อกไก่ย่างซอสพริกไทยดำ กับสลัดควินัวอกไก่ เพิ่มเข้าทุกแพ็กแล้ว',
      permalink: 'https://www.facebook.com/',
    },
  ]
  const comments: MComment[] = [
    {
      id: 'c1', post: 'p1', name: 'Pim Chanok', initial: 'P', text: 'สนใจค่ะ ราคาเท่าไหร่คะ', at: t(4.3), intent: 'buy', summary: 'ถามราคา',
      unread: false, conv: 'c406',
      draftPub: 'ขอบคุณที่สนใจนะคะ คุณ Pim แอดมินขอส่งรายละเอียดให้ทางแชทนะคะ',
      draftDm: 'สวัสดีค่ะ คุณ Pim ขอบคุณที่สนใจแพ็ก 7 วันนะคะ เลือกเมนูเองได้ค่ะ ดูเมนูและสั่งซื้อได้ที่ cleanfit.cloud/order สะดวกให้ส่งแถวไหนคะ แอดมินเช็กค่าส่งให้ค่ะ',
    },
    {
      id: 'c3', post: 'p1', name: 'Ton Kittipat', initial: 'T', text: 'สั่งไปเมื่อวาน ของยังไม่มาเลยครับ', at: t(2.5), intent: 'complaint',
      summary: 'แจ้งปัญหา', note: 'เรื่องร้องเรียนส่งให้พนักงานเสมอ AI ไม่ตอบเอง', unread: true, pos: true, conv: 'c406',
      draftPub: 'ขออภัยในความไม่สะดวกนะคะ คุณ Ton แอดมินขอติดต่อทางแชทเพื่อช่วยดูให้นะคะ',
      draftDm: 'สวัสดีค่ะ คุณ Ton ขออภัยที่ของยังไม่ถึงนะคะ รบกวนแจ้งชื่อหรือเบอร์โทรที่ใช้สั่ง แอดมินจะรีบตรวจสอบให้ค่ะ',
    },
    {
      id: 'c7', post: 'p1', name: 'Like Boost 99', initial: 'L', text: 'รับปั๊มไลก์ ยอดวิว ราคาถูก ทักเลย', at: t(0.5), intent: 'spam',
      summary: 'ดูเป็นสแปม', unread: true, conv: 'c406',
    },
    {
      id: 'c2', post: 'p2', name: '@nok.pakamas', initial: 'N', text: 'ส่งต่างจังหวัดได้ไหมคะ อยู่ขอนแก่น', at: t(2.7), intent: 'buy',
      summary: 'ถามพื้นที่ส่ง', unread: false, conv: 'c406',
      draftPub: 'ขอบคุณที่สนใจนะคะ แอดมินขอส่งรายละเอียดการจัดส่งให้ทางแชทนะคะ',
      draftDm: 'สวัสดีค่ะ ส่งขอนแก่นได้ค่ะ จัดส่งผ่าน DHL รบกวนขอรหัสไปรษณีย์ด้วยนะคะ แอดมินเช็กค่าส่งและวันที่ได้รับให้ค่ะ',
    },
    {
      id: 'c8', post: 'p2', name: '@aom.chutima', initial: 'A', text: 'มีโปรไหมคะ ถ้าสั่ง 2 แพ็ก', at: t(1.3), intent: 'buy', summary: 'ถามโปรโมชั่น',
      unread: true, conv: 'c406',
      draftPub: 'ขอบคุณที่สนใจนะคะ แอดมินขอส่งรายละเอียดให้ทางแชทนะคะ',
      draftDm: 'สวัสดีค่ะ ขอบคุณที่สนใจเมนูเจนะคะ ถ้าสั่ง 2 แพ็ก แอดมินขอเช็กโปรที่ใช้ได้ตอนนี้ให้ก่อนนะคะ สะดวกให้ส่งแถวไหนคะ',
    },
    {
      id: 'c4', post: 'p3', name: 'Mint Siriya', initial: 'M', text: 'เมนูนี้มีแบบไม่ใส่ถั่วไหมคะ', at: t(1.5), intent: 'question', summary: 'ถามเรื่องแพ้อาหาร',
      note: 'เรื่องแพ้อาหาร/สุขภาพ ให้พนักงานตรวจร่างก่อนส่งเสมอ', unread: false, conv: 'c406',
      draftPub: 'ขอบคุณที่สอบถามนะคะ คุณ Mint แอดมินขอส่งรายละเอียดให้ทางแชทนะคะ',
      draftDm: 'สวัสดีค่ะ คุณ Mint ถ้าแพ้ถั่ว แอดมินเปลี่ยนเป็นเมนูที่ไม่มีถั่วให้ได้ค่ะ รบกวนบอกหน่อยนะคะว่าแพ้ถั่วชนิดไหนบ้าง',
    },
    {
      id: 'c6', post: 'p3', name: 'Fah Jiraporn', initial: 'F', text: '@Ploy Wanida อันนี้น่ากินอะ', at: t(0.8), intent: 'social',
      summary: 'แท็กเพื่อน ไม่ได้ถาม', note: 'ตอบสั้นขอบคุณ หรือกดไม่ต้องตอบก็ได้', unread: true, conv: 'c406',
      draftPub: 'ขอบคุณมากนะคะ คุณ Fah', draftDm: 'สวัสดีค่ะ คุณ Fah ขอบคุณที่สนใจเมนูใหม่ของ Cleanfit นะคะ สนใจเรื่องไหนบอกแอดมินได้เลยนะคะ',
    },
    // answered
    {
      id: 'd1', post: 'p1', name: 'Jane Supansa', initial: 'J', text: 'สั่งยังไงคะ', at: t(37), intent: 'buy', summary: 'ถามวิธีสั่ง', unread: false,
      pos: true, conv: 'c406', dmConv: 'c101',
      draftPub: 'ขอบคุณที่สนใจนะคะ คุณ Jane แอดมินขอส่งรายละเอียดให้ทางแชทนะคะ',
      dm: { text: 'สวัสดีค่ะ คุณ Jane สั่งผ่านลิงก์ cleanfit.cloud/order ได้เลยค่ะ หรือบอกแพ็กที่สนใจ แอดมินลงออเดอร์ให้ได้ค่ะ', at: t(28), by: 'jit' },
    },
    {
      id: 'd4', post: 'p1', name: 'Praew Nicha', initial: 'P', text: 'ขอเมนูทั้งหมดหน่อยค่ะ', at: t(7), intent: 'buy', summary: 'ถามเมนู', unread: false,
      conv: 'c406', dmConv: 'c102',
      pub: { text: 'ส่งเมนูทั้งหมดไปทางแชทแล้วนะคะ ตรวจกล่องข้อความได้เลยค่ะ', at: t(5), by: 'ploy' },
      dm: { text: 'สวัสดีค่ะ คุณ Praew ดูเมนูทั้งหมดของสัปดาห์นี้ได้ที่ cleanfit.cloud/order ค่ะ สนใจแพ็กไหนบอกแอดมินได้เลยค่ะ', at: t(5), by: 'ploy' },
    },
    {
      id: 'd2', post: 'p2', name: '@kwan.thidarat', initial: 'K', text: 'เมนูเจมีกี่อย่างคะ', at: t(48), intent: 'question', summary: 'ถามเมนู',
      unread: false, conv: 'c406',
      draftDm: 'สวัสดีค่ะ เมนูเจสัปดาห์นี้ดูได้ครบที่ cleanfit.cloud/order ค่ะ สนใจแพ็กไหนบอกแอดมินได้เลยนะคะ',
      pub: { text: 'มีหลายเมนูเลยค่ะ แอดมินขอส่งรายการเมนูเจให้ทางแชทนะคะ', at: t(44), by: 'siri' },
    },
    {
      id: 'd5', post: 'p2', name: '@oat.krit', initial: 'O', text: 'ส่งลาดพร้าวกี่โมงครับ', at: t(24), intent: 'buy', summary: 'ถามวันได้ของ',
      unread: false, pos: true, conv: 'c406', dmConv: 'c103',
      pub: { text: 'ส่งลาดพร้าวได้ค่ะ แอดมินขอแจ้งรอบส่งทางแชทนะคะ', at: t(21), by: 'siri' },
      dm: { text: 'สวัสดีค่ะ ส่งลาดพร้าวได้ค่ะ เลือกรอบส่งได้ตอนสั่ง แอดมินส่งลิงก์สั่งซื้อให้นะคะ', at: t(21), by: 'siri' },
    },
    {
      id: 'd3', post: 'p3', name: 'Arm Pongsak', initial: 'A', text: 'น่ากินมากครับ', at: t(78), intent: 'social', summary: 'ชม/ทักทาย ไม่ได้ถาม',
      unread: false, conv: 'c406', skip: { at: t(76), by: 'jit' },
    },
    // hidden
    {
      id: 'h1', post: 'p1', name: 'รวยไว 24 ชม.', initial: 'ร', text: 'ลงทุนน้อย รายได้ดี สนใจทักมา', at: t(32), intent: 'spam', summary: 'ดูเป็นสแปม',
      unread: false, conv: 'c406', hidden: { by: 'jit' },
    },
    {
      id: 'h2', post: 'p2', name: '@shop.deal.hub', initial: 'S', text: 'ขายผู้ติดตาม ราคาถูก', at: t(13), intent: 'spam', summary: 'ดูเป็นสแปม',
      unread: false, conv: 'c406', hidden: { by: 'siri' },
    },
  ]
  return { posts, comments }
}

export interface MockCommentsOptions {
  latencyMs?: number
  clock?: () => number
  me?: string
}

export class MockCommentsApi implements CommentsApi {
  posts: MPost[]
  comments: MComment[]
  settings: CommentSettings = { flow_public: true, flow_private: true, ai: true }
  /** Tests: the next reply/private reply fails like the API does (e.g. 502 "เพจยังไม่ได้ให้สิทธิ์…"). */
  rejectNext: { status: number; detail: string } | null = null
  private readonly latency: number
  private readonly now: () => number
  private readonly me: string
  private sentByClient = new Map<string, string>()

  constructor(opts: MockCommentsOptions = {}) {
    this.now = opts.clock ?? (() => Date.now())
    this.latency = opts.latencyMs ?? 120
    this.me = opts.me ?? 'ploy'
    const s = seed(this.now())
    this.posts = s.posts
    this.comments = s.comments
  }

  private async delay(): Promise<void> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
  }

  private stateOf(c: MComment): CommentState {
    if (c.hidden) return 'hidden'
    if (c.pub || c.dm || c.skip) return 'done'
    return 'todo'
  }

  private post(id: string): MPost {
    const p = this.posts.find((x) => x.id === id)
    if (!p) throw new ApiError(404, 'ไม่พบโพสต์')
    return p
  }

  private find(id: string): MComment {
    const c = this.comments.find((x) => x.id === id)
    if (!c) throw new ApiError(404, 'ไม่พบคอมเมนต์นี้')
    return c
  }

  private item(c: MComment): CommentItem {
    const p = this.post(c.post)
    const state = this.stateOf(c)
    return {
      id: c.id,
      channel: p.channel,
      state,
      author: { name: c.name, initial: c.initial },
      text: c.text,
      has_media: false,
      created_at: iso(c.at),
      edited: false,
      unread: c.unread && state === 'todo',
      replied: !!c.pub,
      private_replied: !!c.dm,
      skipped: !!c.skip,
      hidden: !!c.hidden,
      intent: this.settings.ai ? INTENTS[c.intent] : null,
      conversation_id: c.conv,
      post: { id: p.id, title: p.title, is_ad: p.is_ad },
    }
  }

  private matches(c: MComment, q: CommentListQuery): boolean {
    const state = this.stateOf(c)
    const tabOk = q.tab === 'all' || q.tab === state
    const chOk = q.channel === 'all' || this.post(c.post).channel === q.channel
    const postOk = !q.post || c.post === q.post
    const text = (q.q ?? '').trim().toLowerCase()
    const qOk = text.length < 2 || c.text.toLowerCase().includes(text) || c.name.toLowerCase().includes(text)
    return tabOk && chOk && postOk && qOk
  }

  private countsNow(scope: (c: MComment) => boolean = () => true): CommentCountsResponse {
    const mine = this.comments.filter(scope)
    const todo = mine.filter((c) => this.stateOf(c) === 'todo')
    const oldest = todo.reduce<number | null>((m, c) => (m === null || c.at < m ? c.at : m), null)
    return {
      todo: todo.length,
      done: mine.filter((c) => this.stateOf(c) === 'done').length,
      hidden: mine.filter((c) => this.stateOf(c) === 'hidden').length,
      oldest_todo_at: oldest === null ? null : iso(oldest),
      sources: [
        { channel: 'fb', connected: true, mode: 'webhook' },
        { channel: 'ig', connected: true, mode: 'webhook' },
      ],
    }
  }

  async list(q: CommentListQuery): Promise<CommentPage> {
    await this.delay()
    const rows = this.comments.filter((c) => this.matches(c, q))
    rows.sort((a, b) => (q.tab === 'todo' ? a.at - b.at : b.at - a.at))
    const scope = (c: MComment) => (q.channel === 'all' || this.post(c.post).channel === q.channel) && (!q.post || c.post === q.post)
    const { sources: _s, ...counts } = this.countsNow(scope)
    return clone({ items: rows.map((c) => this.item(c)), next_cursor: null, counts })
  }

  async counts(): Promise<CommentCountsResponse> {
    await this.delay()
    return clone(this.countsNow())
  }

  private node(c: MComment): ThreadNode {
    const by = (k: string | null | undefined) => (k ? STAFF[k] ?? { id: k, name: k } : null)
    return {
      id: c.id,
      state: this.stateOf(c),
      author: { name: c.name, initial: c.initial },
      text: c.text,
      has_media: false,
      created_at: iso(c.at),
      edited: false,
      hidden: !!c.hidden,
      removed: false,
      private_reply: c.dm ? { at: iso(c.dm.at), by: by(c.dm.by) } : null,
      skipped: c.skip ? { at: iso(c.skip.at), by: by(c.skip.by) } : null,
      hidden_by: c.hidden ? by(c.hidden.by) : null,
      replies: c.pub ? [{ id: `${c.id}-r`, text: c.pub.text, created_at: iso(c.pub.at), by: by(c.pub.by), hidden: false }] : [],
    }
  }

  private threadOf(c: MComment): CommentThread {
    const all = this.comments.filter((x) => x.post === c.post).sort((a, b) => a.at - b.at)
    let win = all.slice(-4)
    if (!win.includes(c)) win = [c, ...all.slice(-3)]
    win.sort((a, b) => a.at - b.at)
    const hiddenCount = all.length - win.length
    // the mock pretends the post had more comments before the ones seeded
    const older = hiddenCount + 17
    return { items: win.map((x) => this.node(x)), older_count: older, before: older ? `before-${c.id}` : null }
  }

  async get(id: string): Promise<CommentDetail> {
    await this.delay()
    return clone(this.detail(this.find(id)))
  }

  private detail(c: MComment): CommentDetail {
    const p = this.post(c.post)
    const by = (k: string | null | undefined) => (k ? STAFF[k] ?? { id: k, name: k } : null)
    const now = this.now()
    const expires = c.at + 7 * DAY
    const privateReason = c.dm
      ? 'ส่งแชทส่วนตัวของคอมเมนต์นี้แล้ว (Meta ให้ส่งได้ 1 ข้อความต่อคอมเมนต์)'
      : now > expires - 2 * MIN
        ? 'เกิน 7 วันหลังคอมเมนต์ ส่งแชทส่วนตัวไม่ได้แล้ว (กติกา Meta)'
        : null
    const all = this.comments.filter((x) => x.post === c.post)
    const ai = this.settings.ai
    const pubCanned: CannedLine[] =
      c.intent === 'spam'
        ? []
        : c.intent === 'complaint'
          ? [
              { id: 'pub_sorry', label: 'ขออภัย ติดต่อทางแชท', text: 'ขออภัยในความไม่สะดวกนะคะ แอดมินขอติดต่อทางแชทเพื่อช่วยดูให้นะคะ' },
              { id: 'pub_thanks_report', label: 'ขอบคุณที่แจ้ง', text: 'ขอบคุณที่แจ้งนะคะ แอดมินรีบตรวจสอบให้ค่ะ' },
            ]
          : c.intent === 'social'
            ? [{ id: 'pub_thanks', label: 'ขอบคุณค่ะ', text: 'ขอบคุณมากนะคะ' }, PUB_CHAT]
            : [PUB_CHAT, { id: 'pub_interest', label: 'ขอบคุณที่สนใจ', text: 'ขอบคุณที่สนใจนะคะ แอดมินขอส่งรายละเอียดให้ทางแชทนะคะ' }]
    const dmCanned: CannedLine[] =
      c.intent === 'spam'
        ? []
        : c.intent === 'complaint'
          ? [ASK_ORDER, WAIT_CHECK]
          : c.intent === 'social'
            ? [THANKS, ORDER_LINK]
            : c.intent === 'question'
              ? [WAIT_CHECK, ORDER_LINK]
              : [ORDER_LINK, ASK_ZIP]
    return {
      comment: {
        ...this.item(c),
        platform_id: `${p.id}_${c.id}`,
        is_reply: false,
        reply: c.pub ? { text: c.pub.text, at: iso(c.pub.at), by: by(c.pub.by) } : null,
        private_reply: c.dm ? { text: c.dm.text, at: iso(c.dm.at), by: by(c.dm.by) } : null,
        skip: c.skip ? { at: iso(c.skip.at), by: by(c.skip.by) } : null,
        hidden_by: c.hidden ? by(c.hidden.by) : null,
        dm_conversation_id: c.dmConv ?? null,
        unconfirmed_reply: null,
      },
      post: {
        id: p.id,
        channel: p.channel,
        platform_id: p.id,
        title: p.title,
        caption: p.caption,
        permalink: p.permalink,
        thumbnail_url: null,
        is_ad: p.is_ad,
        ad_title: p.ad_title,
        posted_at: iso(p.posted),
        page_name: 'Cleanfit Delivery',
        total: all.length + 17,
        todo: all.filter((x) => this.stateOf(x) === 'todo').length,
        details_loaded: true,
      },
      thread: this.threadOf(c),
      rules: {
        public: { can: true, reason: null },
        private: { can: privateReason === null, reason: privateReason, expires_at: iso(expires) },
      },
      pos: { linked: !!c.pos, customer_id: c.pos ? '24318' : null },
      ai: {
        on: ai,
        state: ai ? 'done' : 'off',
        intent: ai ? INTENTS[c.intent] : null,
        confidence: ai ? 0.91 : null,
        summary: ai ? c.summary : null,
        note: ai ? (c.note ?? null) : null,
        public_draft: ai ? (c.draftPub ?? null) : null,
        private_draft: ai ? (c.draftDm ?? null) : null,
        public_canned: pubCanned,
        private_canned: dmCanned,
        chat_draft_applies: false,
        draft_source: { public: 'template', private: 'ai' },
      },
      settings: { ...this.settings },
    }
  }

  async thread(id: string): Promise<CommentThread> {
    await this.delay()
    const c = this.find(id)
    // older comments are not part of the mock: return an empty page and stop offering more
    void c
    return { items: [], older_count: 0, before: null }
  }

  private checkReject(): void {
    if (this.rejectNext) {
      const r = this.rejectNext
      this.rejectNext = null
      throw new ApiError(r.status, r.detail)
    }
  }

  async reply(id: string, body: CommentReplyBody): Promise<CommentDetail> {
    await this.delay()
    const c = this.find(id)
    const seen = this.sentByClient.get(`pub:${body.client_id}`)
    if (!seen) {
      this.checkReject()
      if (!body.text.trim()) throw new ApiError(422, 'ข้อความว่าง')
      c.pub = { text: body.text.trim(), at: this.now(), by: this.me }
      c.unread = false
      this.sentByClient.set(`pub:${body.client_id}`, id)
    }
    return clone(this.detail(c))
  }

  async privateReply(id: string, body: CommentReplyBody): Promise<CommentDetail> {
    await this.delay()
    const c = this.find(id)
    const seen = this.sentByClient.get(`dm:${body.client_id}`)
    if (!seen) {
      const d = this.detail(c)
      if (!d.rules.private.can) throw new ApiError(422, d.rules.private.reason ?? 'ส่งไม่ได้')
      this.checkReject()
      if (!body.text.trim()) throw new ApiError(422, 'ข้อความว่าง')
      c.dm = { text: body.text.trim(), at: this.now(), by: this.me }
      c.dmConv = c.dmConv ?? 'c101'
      c.unread = false
      this.sentByClient.set(`dm:${body.client_id}`, id)
    }
    return clone(this.detail(c))
  }

  async hide(id: string, hidden: boolean): Promise<CommentDetail> {
    await this.delay()
    const c = this.find(id)
    c.hidden = hidden ? { by: this.me } : undefined
    c.unread = false
    return clone(this.detail(c))
  }

  async skip(id: string, skip: boolean): Promise<CommentDetail> {
    await this.delay()
    const c = this.find(id)
    c.skip = skip ? { at: this.now(), by: this.me } : undefined
    c.unread = false
    return clone(this.detail(c))
  }

  async ofConversation(conversationId: string): Promise<string | null> {
    await this.delay()
    const mine = this.comments.filter((c) => c.conv === conversationId)
    const todo = mine.filter((c) => this.stateOf(c) === 'todo').sort((a, b) => a.at - b.at)
    return (todo[0] ?? mine.sort((a, b) => b.at - a.at)[0])?.id ?? null
  }

  async getSettings(): Promise<CommentSettings> {
    await this.delay()
    return { ...this.settings, can_edit: true }
  }

  async putSettings(patch: Partial<Omit<CommentSettings, 'can_edit'>>): Promise<CommentSettings> {
    await this.delay()
    const next = { ...this.settings, ...patch }
    if (!next.flow_public && !next.flow_private) {
      throw new ApiError(422, 'ต้องเลือกอย่างน้อย 1 ทาง: ตอบทางแชท หรือ ตอบใต้คอมเมนต์')
    }
    this.settings = next
    return { ...this.settings, can_edit: true }
  }

  /** Mock only: mark a thread read (the real screen calls POST /conversations/:id/read). */
  markRead(id: string): void {
    const c = this.comments.find((x) => x.id === id)
    if (c) c.unread = false
  }
}
